import { useState, useMemo } from "react";
import { useTranslate } from "@refinedev/core";

import { User } from "@modules/users/types/user";
import { ApiResponse } from "@shared/types";
import { usePaginatedTable, useQueryParams } from "@shared/hooks";
import { useFetchResourcesByIdsQuery } from "@shared/services/queries";
import { Agreement } from "@modules/agreements/types";

export function useUsersList() {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const translate = useTranslate();
  const { getQueryParams } = useQueryParams();

  const { rows, loading, refetch, ...tableProps } = usePaginatedTable<User>({
    resource: "v1/user_admin",
    filters: getQueryParams(),
  });

  const agreementIds = useMemo(
    () => [...new Set(rows?.map((user) => user.active_agreement).filter((id): id is number => id !== undefined && id !== null))],
    [rows]
  );

  const { data: agreements } = useFetchResourcesByIdsQuery<ApiResponse<Agreement>>({
    resource: "v1/agreement_admin",
    ids: agreementIds,
  });

  const agreementMap = useMemo(() => {
    return agreements?.results?.reduce<Record<number, string>>((acc, agreement) => {
      if (agreement.id !== undefined && agreement.name !== undefined) {
        acc[agreement.id] = agreement.name;
      }
      return acc;
    }, {}) ?? {};
  }, [agreements]);

  const dataToExport = useMemo(() => {
    return selectedRows.length > 0
      ? rows.filter((row) => selectedRows.includes(row.id))
      : rows;
  }, [selectedRows, rows]);

  return {
    rows,
    loading,
    tableProps,
    selectedRows,
    agreementMap,
    dataToExport,
    refetch,
    setSelectedRows,
    translate,
  };
}
